import { extractRowsForGraphType } from "./extractors";

/** Graph types where tag mode (all / individual / grouped) filters or relabels rows. */
const GRAPH_TYPES_WITH_TAG_MODE = new Set([
  "tagged-wr-vs-date",
  "wr-vs-length",
  "weight-vs-length",
]);

/**
 * Apply tag mode (all / individual / grouped) to graph rows.
 *
 * @param {Array<object>} data
 * @param {string} graphType
 * @param {{ selectedTagMode?: { value: string }, selectedTags?: Array<{ value: string }>, includeUntagged?: boolean }} ctx
 * @returns {Array<object>}
 */
export function applyTagModeToGraphRows(data, graphType, ctx) {
  if (!Array.isArray(data) || data.length === 0) return data || [];
  if (!GRAPH_TYPES_WITH_TAG_MODE.has(graphType)) return data;

  const { selectedTagMode, selectedTags, includeUntagged } = ctx;

  if (selectedTagMode?.value === "individual") {
    const tagSet = new Set((selectedTags || []).map((t) => t.value));
    if (tagSet.size === 0) {
      return [];
    }
    return data
      .filter((d) => d.isTagged)
      .filter((d) => tagSet.has(d.tagNumber))
      .map((d) => ({
        ...d,
        species: d.tagNumber ? `Tag ${d.tagNumber}` : d.species,
      }));
  }

  if (selectedTagMode?.value === "grouped") {
    return data
      .filter((d) => d.isTagged || includeUntagged)
      .map((d) => ({
        ...d,
        species: d.isTagged ? "Tagged Fish" : "Untagged Fish",
      }));
  }

  return data;
}

/**
 * @param {Array} pondDetails - enriched pond detail rows
 * @param {string} graphType - graph id
 * @param {object} ctx - { selectedUnit, selectedFish, selectedTagMode, selectedTags, includeUntagged, formatDateTime }
 * @returns {Array} graph rows (sampling only; tag mode applied after merging with stocking in FishDataGraphView)
 */
export function buildGraphDataFromPondDetails(pondDetails, graphType, ctx) {
  let data = [];

  pondDetails.forEach((detail) => {
    data.push(...extractRowsForGraphType(graphType, detail, ctx));
  });

  if (graphType === "avg-wr-vs-date" || graphType === "tagged-wr-vs-date") {
    data.sort((a, b) => new Date(a.date) - new Date(b.date));
  }

  return data;
}
